import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const ARAgingWidget = ({ viewType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getARaging(viewType);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching AR aging:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getBucketColor = (index) => {
    const colors = [
      { bar: 'bg-yellow-400', text: 'text-yellow-700', bg: 'bg-yellow-50' },
      { bar: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50' },
      { bar: 'bg-red-400', text: 'text-red-700', bg: 'bg-red-50' },
      { bar: 'bg-red-600', text: 'text-red-800', bg: 'bg-red-100' },
    ];
    return colors[index];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const maxAmount = data ? Math.max(...data.buckets.map(b => b.amount), 1) : 1;

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Clock className="h-5 w-5 text-orange-500 mr-2" />
            Accounts Receivable Aging
          </h3>
          {data && data.total_outstanding > 0 && (
            <button
              onClick={() => navigate('/billing?status=overdue')}
              className="text-sm text-yellow-600 hover:text-yellow-800 font-medium"
            >
              View All
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {!data || data.total_count === 0 ? (
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <Clock className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600 font-medium">No overdue invoices</p>
            <p className="text-sm text-gray-500 mt-1">All caught up!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total Outstanding */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-orange-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">Total Outstanding</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">{formatCurrency(data.total_outstanding)}</span>
                <span className="text-sm text-gray-500 ml-2">({data.total_count} invoices)</span>
              </div>
            </div>

            {/* Aging Buckets */}
            <div className="space-y-3">
              {data.buckets.map((bucket, index) => {
                const colors = getBucketColor(index);
                const widthPct = Math.max((bucket.amount / maxAmount) * 100, 2);
                return (
                  <div key={bucket.label}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">{bucket.label}</span>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${colors.text}`}>
                          {formatCurrency(bucket.amount)}
                        </span>
                        {bucket.count > 0 && (
                          <span className="text-xs text-gray-400 ml-1">({bucket.count})</span>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${colors.bar}`}
                        style={{ width: bucket.amount > 0 ? `${widthPct}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ARAgingWidget;
